import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Tag } from "antd";

import {
  extendSession,
  getProfile, 
  hasTokenExpired,
  logout,
} from "../../security/AppSecurity";

// Unique identifier name for cross-tab communication
const SESSION_CHANNEL_NAME = "auth_session_channel";
// Activity throttle window (5 seconds)
const ACTIVITY_THROTTLE_MS = 5000;

// Session Expiry Configuration
const WARNING_BEFORE_EXPIRY_MS = 60 * 1000; // Show modal 60 seconds before expiry

// Idle Detection Configuration (used only when enableIdleDetection=true)
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;     // 10 minutes - show idle warning
const IDLE_LOGOUT_MS = 15 * 60 * 1000;      // 15 minutes - auto logout

// Storage keys
const SESSION_LOCK_KEY = "session_action_lock";
const EXPIRY_STORAGE_KEY = "session_expiry_timestamp";
const MASTER_TAB_KEY = "session_master_tab_id";
const LAST_ACTIVITY_KEY = "last_user_activity_timestamp";

/**
 * TimerControl Component: Manages session timeout, auto-refresh, and optional idle detection
 * with multi-tab synchronization using a Master/Worker election model.
 *
 * Responsibilities:
 * - Elects a single tab as the "Master" to safely handle Keycloak/API operations
 * - Only Master tab manages countdown, modal visibility, and idle logic
 * - Background worker tabs follow Master's state to prevent flickering/race conditions
 * - Silently forwards user activity from worker tabs to Master via BroadcastChannel
 * - Supports optional idle detection via props (disabled by default)
 * - Suspends modal triggers globally while Keycloak token extension is in progress
 */
export const TimerControl = ({
  enableIdleDetection = false,        // Idle detection is OFF by default
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ minutes: 0, seconds: 0 });
  const [isIdleWarning, setIsIdleWarning] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);
  const channelRef = useRef(null);
  
  // Unique identification string for this tab instance
  const tabIdRef = useRef(Math.random().toString(36).substring(2, 11));
  const isOpenRef = useRef(false);
  
  // Blocks all timer and modal evaluations while network operations are pending
  const isAwaitingNetworkRef = useRef(false);

  const totalSeconds = timeLeft.minutes * 60 + timeLeft.seconds;

  let color = "#00c853";
  if (timeLeft.minutes === 0 && totalSeconds <= 40 && totalSeconds > 20) {
    color = "#ff6f00";
  } else if (timeLeft.minutes === 0 && totalSeconds <= 20) {
    color = "#d50000";
  }

  // Helper to sync state and notify worker tabs of visibility changes
  const updateModalState = (open, broadcast = true) => {
    setIsOpen(open);
    isOpenRef.current = open;
    
    if (isMasterTab() && broadcast && channelRef.current) {
      channelRef.current.postMessage({
        type: open ? "SESSION_MODAL_OPENED" : "SESSION_MODAL_CLOSED"
      });
    }
  };

  // Verifies if this tab is currently the elected Master
  const isMasterTab = useCallback(() => {
    const currentMaster = localStorage.getItem(MASTER_TAB_KEY);
    return currentMaster === tabIdRef.current || !currentMaster;
  }, []);

  const claimMasterStatus = useCallback(() => {
    localStorage.setItem(MASTER_TAB_KEY, tabIdRef.current);
  }, []);

  const updateLastActivity = () => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  };

  const handleLogout = () => {
    updateModalState(false, false);
    setIsIdleWarning(false);
    setTimeLeft({ minutes: 0, seconds: 0 });
    isAwaitingNetworkRef.current = false;

    localStorage.removeItem(SESSION_LOCK_KEY);
    localStorage.removeItem(EXPIRY_STORAGE_KEY);
    localStorage.removeItem(MASTER_TAB_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);

    logout();
  };

  /**
   * Only executed by the elected Master Tab.
   * Safely performs the Keycloak token refresh operation.
   */
  const executeMasterSessionExtension = async () => {
    if (isRefreshingRef.current || localStorage.getItem(SESSION_LOCK_KEY) === "locked") {
      return;
    }
    
    isRefreshingRef.current = true;
    isAwaitingNetworkRef.current = true;
    localStorage.setItem(SESSION_LOCK_KEY, "locked");

    // Inform all tabs to freeze UI and hide modals during refresh
    channelRef.current?.postMessage({ type: "SESSION_AWAITING_NETWORK" });
    updateModalState(false, true);

    try {
      await extendSession();
      
      const profile = getProfile();
      if (profile?.expiryTime) {
        const newExpiry = profile.expiryTime * 1000;
        localStorage.setItem(EXPIRY_STORAGE_KEY, String(newExpiry));

        channelRef.current?.postMessage({ 
          type: "SESSION_EXTENDED", 
          newExpiry 
        });
      }
    } catch (err) {
      console.error("Failed to extend Keycloak session", err);
    } finally {
      isRefreshingRef.current = false;
      isAwaitingNetworkRef.current = false;
      localStorage.removeItem(SESSION_LOCK_KEY);
    }
  };

  const broadcast = (type, payload = {}) => {
    channelRef.current?.postMessage({ type, ...payload });
  };

  /**
   * Checks idle time and triggers appropriate actions (warning or logout).
   * Only active when enableIdleDetection is true.
   */
  const checkIdleStatus = (currentTime) => {
    if (!enableIdleDetection) return false;

    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || currentTime;
    const idleTime = currentTime - lastActivity;

    if (idleTime >= IDLE_LOGOUT_MS) {
      broadcast("SESSION_LOGOUT");
      handleLogout();
      return true;
    }

    if (idleTime >= IDLE_TIMEOUT_MS && !isOpenRef.current) {
      setIsIdleWarning(true);
      updateModalState(true, true);
      return true;
    }

    return false;
  };

  useEffect(() => {
    // Master tab election
    if (!localStorage.getItem(MASTER_TAB_KEY)) {
      claimMasterStatus();
    }

    // Initialize expiry timestamp
    const initialProfile = getProfile();
    if (initialProfile?.expiryTime && !localStorage.getItem(EXPIRY_STORAGE_KEY)) {
      localStorage.setItem(EXPIRY_STORAGE_KEY, String(initialProfile.expiryTime * 1000));
    }

    // Initialize last activity only if idle detection is enabled
    if (enableIdleDetection && !localStorage.getItem(LAST_ACTIVITY_KEY)) {
      updateLastActivity();
    }

    channelRef.current = new BroadcastChannel(SESSION_CHANNEL_NAME);

    channelRef.current.onmessage = (event) => {
      if (!event.data) return;

      switch (event.data.type) {
        case "REQUEST_SESSION_EXTENSION":
          if (isMasterTab()) {
            executeMasterSessionExtension();
          }
          break;

        case "SESSION_AWAITING_NETWORK":
          isAwaitingNetworkRef.current = true;
          updateModalState(false, false);
          break;

        case "SESSION_EXTENDED":
          isAwaitingNetworkRef.current = false;
          if (event.data.newExpiry) {
            localStorage.setItem(EXPIRY_STORAGE_KEY, String(event.data.newExpiry));
          }
          updateModalState(false, false);
          break;

        case "SESSION_LOGOUT":
          handleLogout();
          break;

        case "SESSION_MODAL_OPENED":
          if (!isMasterTab() && !isAwaitingNetworkRef.current) {
            updateModalState(true, false);
          }
          break;

        case "SESSION_MODAL_CLOSED":
          if (!isMasterTab()) {
            updateModalState(false, false);
          }
          break;

        case "SYNC_TIME_LEFT":
          if (!isMasterTab() && event.data.timeLeft && !isAwaitingNetworkRef.current) {
            setTimeLeft(event.data.timeLeft);
          }
          break;

        default:
          break;
      }
    };

    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > ACTIVITY_THROTTLE_MS) {
        if (enableIdleDetection) {
          updateLastActivity();
        }

        if (!isOpenRef.current && !hasTokenExpired() && !isAwaitingNetworkRef.current) {
          if (isMasterTab()) {
            executeMasterSessionExtension();
          } else {
            broadcast("REQUEST_SESSION_EXTENSION");
          }
        }
      }
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart", "focus"];
    events.forEach((ev) => window.addEventListener(ev, handleUserActivity));

    // Main timer loop - runs on all tabs but heavy logic only on Master
    const updateTimer = () => {
      if (isAwaitingNetworkRef.current) return;

      const storedExpiry = localStorage.getItem(EXPIRY_STORAGE_KEY);
      if (!storedExpiry || hasTokenExpired()) {
        broadcast("SESSION_LOGOUT");
        handleLogout();
        return;
      }

      const tokenExpiryTime = Number.parseInt(storedExpiry, 10);
      const diff = tokenExpiryTime - Date.now();

      if (diff <= 0) {
        broadcast("SESSION_LOGOUT");
        handleLogout();
        return;
      }

      const now = Date.now();

      // Idle detection (only performed by Master tab and when enabled)
      if (isMasterTab() && enableIdleDetection) {
        if (checkIdleStatus(now)) return;
      }

      if (isMasterTab()) {
        const totalSeconds = Math.floor(diff / 1000);
        const mins = Math.floor(totalSeconds / 60) % 60;
        const secs = totalSeconds % 60;

        const newTime = { minutes: mins, seconds: secs };

        setTimeLeft(newTime);
        broadcast("SYNC_TIME_LEFT", { timeLeft: newTime });

        // Show session expiry warning (skip if idle warning is active)
        if (diff <= WARNING_BEFORE_EXPIRY_MS && !isOpenRef.current && !isIdleWarning) {
          updateModalState(true, true);
        }
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handleUserActivity));
      clearInterval(interval);
      channelRef.current?.close();
      
      if (isMasterTab()) {
        localStorage.removeItem(MASTER_TAB_KEY);
      }
    };
  }, [isMasterTab, claimMasterStatus, enableIdleDetection, isIdleWarning]);

  const onOk = () => {
    if (enableIdleDetection) {
      updateLastActivity(); // Reset idle timer
    }
    setIsIdleWarning(false);

    if (isMasterTab()) {
      executeMasterSessionExtension();
    } else {
      updateModalState(false, false);
      broadcast("REQUEST_SESSION_EXTENSION");
    }
  };

  const onCancel = () => {
    if (localStorage.getItem(SESSION_LOCK_KEY) === "locked") return;
    localStorage.setItem(SESSION_LOCK_KEY, "locked");
    
    broadcast("SESSION_LOGOUT");
    handleLogout();
  };

  const formatTime = (num) => String(num).padStart(2, "0");

  const modalTitle = isIdleWarning 
    ? "Session Idle Warning" 
    : "Your Login session is about to expire";

  const modalContent = isIdleWarning ? (
    <p>You have been inactive for a while.<br />Your session will be logged out soon if you remain idle.</p>
  ) : (
    <p>
      Your session will expire in{" "}
      <Tag color={color} style={{ fontWeight: "bold" }}>
        {formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
      </Tag>
    </p>
  );

  return (
    <Modal
      title={modalTitle}
      width={440}
      open={isOpen}
      onOk={onOk}
      onCancel={onCancel}
      cancelText="Logout"
      okText={isIdleWarning ? "Stay Logged In" : "Extend Session"}
      closable={false}
      maskClosable={false}
      style={{ zIndex: 99999 }}
    >
      {modalContent}
    </Modal>
  );
};